import {checkedRequest as checkedOpnopolyRequest, checkedCapability as checkedOpnopolyCapability} from './opnopoly-client.js';
import {checkedTutorialRequest} from './opnopoly-tutorial-client.js';
import axios from 'axios';
import Lambda from './utilities/aws-lambda';
import {getFullPath} from './kaisaku-api';
import {OPNOPOLY_UI_RELEASE_READY} from './opnopoly-release';
import {onCatalogCompleted} from './utilities/catalog-completion';

export const REWARDS_ENDPOINT = 'https://xjqwjw2vqd.execute-api.ap-southeast-1.amazonaws.com';
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const fail = (message, status = 503) => Object.assign(new Error(message), {status});
const nonnegative = n => Number.isSafeInteger(n) && n >= 0;
const CATALOG_REFRESH_INTERVAL = 60000;

export function checkedConnection(config, identity, now = Date.now()) {
    const r = config && config.rewards;
    if (!r || r.schema !== 'opgames-rewards-sdk-v1' || r.available !== true) throw fail('DISABLED', 409);
    if (r.apiEndpoint !== REWARDS_ENDPOINT || r.userId !== identity.userId || r.gameId !== identity.gameId
        || !r.session || r.session.audience !== 'opgames-rewards' || !uuid.test(r.session.accessToken || '')
        || r.session.accessToken === identity.accessToken || !Number.isSafeInteger(r.session.expiresAt)
        || r.session.expiresAt <= now + 30000 || r.session.expiresAt > now + 16 * 60000) throw fail('INVALID_RESPONSE');
    let monopoly = null;
    try {
        monopoly = checkedOpnopolyCapability(r.monopoly, identity, now);
        if (monopoly.expiresAt > r.session.expiresAt) monopoly = null;
    } catch (_) { /* Missing/disabled activity must not disrupt lottery or ranking. */ }
    return {token: r.session.accessToken, expiresAt: r.session.expiresAt, monopoly};
}

export function checkedActivity(status, notices, userId) {
    if (!status || status.mode !== 'OP_REWARDS' || status.userId !== userId
        || !status.balance || !nonnegative(status.balance.daily) || !nonnegative(status.balance.monthly)
        || typeof status.drawEnabled !== 'boolean' || typeof status.frozen !== 'boolean') throw fail('INVALID_RESPONSE');
    const items = notices && Array.isArray(notices.items) ? notices.items : [];
    return {available: true, tickets: {...status.balance}, drawEnabled: status.drawEnabled,
        frozen: status.frozen, unreadTicketRewards: items.length,
        unreadRankingRewards: items.filter(x => x.source === 'RANK_REWARD').length};
}

// Exact routes consumed by the approved activity pages. No caller-selected host or arbitrary API path.
export function checkedRequest(path, payload) {
    if (path === '/v1/monopoly/tutorial') return checkedTutorialRequest(path, payload);
    if (typeof path === 'string' && path.startsWith('/v1/monopoly/')) return checkedOpnopolyRequest(path, payload);
    if (typeof path !== 'string' || path.length > 512 || !path.startsWith('/api/')) throw fail('INVALID_REQUEST', 400);
    const url = new URL(path, REWARDS_ENDPOINT);
    if (url.origin !== REWARDS_ENDPOINT || url.hash) throw fail('INVALID_REQUEST', 400);
    const allowed = {'/api/status': [], '/api/ticket-notices': [], '/api/ticket-history': ['pool','before'],
        '/api/rankings': ['pool','period'], '/api/result': ['requestId']};
    if (payload === undefined && Object.prototype.hasOwnProperty.call(allowed, url.pathname)) {
        for (const name of url.searchParams.keys()) if (!allowed[url.pathname].includes(name)
            || url.searchParams.getAll(name).length !== 1) throw fail('INVALID_REQUEST', 400);
        return 'GET';
    }
    if (url.search || !payload || typeof payload !== 'object' || Array.isArray(payload)) throw fail('INVALID_REQUEST', 400);
    const keys = Object.keys(payload).sort().join(',');
    if (url.pathname === '/api/draw' && (keys === 'count,pool,requestId' || keys === 'count,pool,requestId,rewardSchema' && payload.rewardSchema === 'opgames-lottery-rewards-v2') && ['daily','monthly'].includes(payload.pool)
        && [1,5,10].includes(payload.count) && /^[-a-zA-Z0-9_]{16,80}$/.test(payload.requestId)) return 'POST';
    if (url.pathname === '/api/ticket-notices/ack' && keys === 'daily,monthly'
        && nonnegative(payload.daily) && nonnegative(payload.monthly)) return 'POST';
    throw fail('INVALID_REQUEST', 400);
}

export default class RewardsApi {
    constructor(identity) {
        this.identity = {...identity}; this.connection = null; this.pending = null; this.disposed = false; this.reads = new Map();
        this.catalogRevision = 0; this.sessionCatalogRevision = 0; this.handledCatalogRevision = 0;
        this.catalogRefresh = null; this.catalogTimer = null; this.lastCatalogRefresh = -Infinity;
        this.capabilityListeners = new Set();
        this.stopCatalog = OPNOPOLY_UI_RELEASE_READY ? onCatalogCompleted(this.identity.accessToken, () => {
            if (!this.disposed) { this.catalogRevision++; this.queueCatalogRefresh(); }
        }) : () => {};
    }
    dispose() {
        this.disposed = true; this.connection = null; this.reads.clear(); this.stopCatalog();
        clearTimeout(this.catalogTimer); this.catalogTimer = null; this.capabilityListeners.clear();
    }
    onCapabilityRefresh(listener) {
        if (this.disposed || typeof listener !== 'function') return () => {};
        this.capabilityListeners.add(listener);
        return () => this.capabilityListeners.delete(listener);
    }
    hasCurrentMonopoly() {
        const capability = this.connection && this.connection.monopoly;
        return !!capability && capability.expiresAt > Date.now() && capability.weekEndsAt > Date.now();
    }
    queueCatalogRefresh() {
        if (this.disposed || this.catalogRefresh || this.catalogTimer !== null || this.hasCurrentMonopoly()
            || (!this.pending && !this.connection)) return;
        // Stock polling may complete repeatedly. Coalesce those signals; an
        // absent capability or failed config never schedules a retry by itself.
        const delay = Math.max(0, this.lastCatalogRefresh + CATALOG_REFRESH_INTERVAL - Date.now());
        if (delay) this.catalogTimer = setTimeout(() => {
            this.catalogTimer = null; this.refreshAfterCatalog();
        }, delay);
        else this.refreshAfterCatalog();
    }
    refreshAfterCatalog() {
        if (this.disposed || this.catalogRefresh) return;
        const job = Promise.resolve().then(async () => {
            // Do not reuse a config request that began before publication, and
            // do not let its older response overwrite the new capability.
            if (this.pending) { try { await this.pending; } catch (_) { /* One later authenticated read is allowed. */ } }
            const revision = this.catalogRevision;
            this.handledCatalogRevision = revision;
            if (this.disposed || this.hasCurrentMonopoly() || this.sessionCatalogRevision >= revision) return;
            this.lastCatalogRefresh = Date.now();
            await this.session(true);
            if (!this.disposed) this.capabilityListeners.forEach(listener => {
                try { listener(); } catch (_) { /* Optional presentation update. */ }
            });
        }).catch(() => { /* Keep capability absent; catalogue/payment flow remains usable. */ }).finally(() => {
            this.catalogRefresh = null;
            if (!this.disposed && this.catalogRevision > this.handledCatalogRevision) this.queueCatalogRefresh();
        });
        this.catalogRefresh = job;
    }
    async session(refresh = false) {
        if (this.disposed) throw fail('UNAUTHORIZED', 401);
        const id = this.identity;
        if ((id.userId && !uuid.test(id.userId)) || !uuid.test(id.gameId || '') || !id.accessToken) throw fail('UNAUTHORIZED', 401);
        const now = Date.now();
        const monopoly = this.connection && this.connection.monopoly;
        const activityExpired = monopoly && (monopoly.expiresAt <= now || monopoly.weekEndsAt <= now);
        if (!refresh && this.connection && this.connection.expiresAt > now + 60000 && !activityExpired) return this.connection;
        if (!this.pending) {
            this.sessionCatalogRevision = this.catalogRevision;
            this.pending = (async () => {
                const res = await Lambda.get(getFullPath('getSdkConfig2/' + encodeURIComponent(id.gameId)), {rewards: '1'}, id.accessToken);
                if (!res || res.status !== 200) {
                    const error = fail(res && res.status === 401 ? 'UNAUTHORIZED' : 'RETRY_SAME_REQUEST', res ? res.status : 503);
                    error.activityFailure = 'SDK';
                    error.httpResponseReceived = !!res;
                    throw error;
                }
                // Native purchase sessions may contain only the token. Resolve
                // the missing ID from this authenticated SDK response, never a
                // URL/cookie guess or another account's cached transaction.
                const resolvedIdentity = id.userId ? id : {...id,
                    userId: res.data && res.data.rewards && res.data.rewards.userId};
                if (!uuid.test(resolvedIdentity.userId || '')) throw fail('INVALID_RESPONSE');
                const connection = checkedConnection(res.data, resolvedIdentity);
                if (this.disposed) throw fail('UNAUTHORIZED', 401);
                if (!id.userId) id.userId = resolvedIdentity.userId;
                this.connection = connection;
                return connection;
            })().finally(() => { this.pending = null; });
        }
        return this.pending;
    }
    async call(path, payload) {
        const method = checkedRequest(path, payload);
        const pathname = new URL(path, REWARDS_ENDPOINT).pathname;
        const sharedRead = method === 'GET' && ['/api/status', '/api/ticket-notices', '/api/rankings'].includes(pathname);
        if (sharedRead && this.reads.has(path)) return this.reads.get(path);
        const request = this.request(path, payload, method);
        if (sharedRead) {
            this.reads.set(path, request);
            const clear = () => { if (this.reads.get(path) === request) this.reads.delete(path); };
            // Both branches clear in-flight work without leaving a rejected cleanup promise.
            request.then(clear, clear);
        }
        return request;
    }
    async request(path, payload, method) {
        const session = await this.session();
        if (path.startsWith('/v1/monopoly/')) checkedOpnopolyCapability(session.monopoly, this.identity, Date.now());
        try {
            const res = await axios.request({url: REWARDS_ENDPOINT + path, method, timeout: 25000,
                headers: {Authorization: 'Bearer ' + session.token, 'Content-Type': 'application/json', ...(path.startsWith('/v1/monopoly/') ? {'X-OPNOPOLY-Contract': 'weekly-free-v1'} : {})},
                data: payload, withCredentials: false});
            if (this.disposed) throw fail('UNAUTHORIZED', 401);
            return res.data;
        } catch (e) {
            const status = e.response ? e.response.status : e.status || 503;
            if (status === 401) this.connection = null;
            // Never retry a draw automatically; the activity page retains the exact requestId.
            const error = fail(e.response && e.response.data && e.response.data.error || e.message || 'RETRY_SAME_REQUEST', status);
            if (error.message === 'CLIENT_UPDATE_REQUIRED') error.code = 'CLIENT_UPDATE_REQUIRED';
            error.activityFailure = 'REWARDS';
            error.httpResponseReceived = !!e.response;
            throw error;
        }
    }
    async activity(onNotices) {
        const statusRequest = this.call('/api/status');
        const noticesRequest = this.call('/api/ticket-notices').catch(() => null);
        const status = await statusRequest;
        const activity = checkedActivity(status, null, this.identity.userId);
        // Optional badges do not delay an entrance whose authenticated status is valid.
        if (typeof onNotices === 'function') {
            noticesRequest.then(notices => {
                if (!this.disposed) onNotices(checkedActivity(status, notices, this.identity.userId));
            }).catch(() => {});
        }
        return activity;
    }
}
